import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import ChangeContextFields from '@/frontend/features/Fde/ChangeContextFields'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import { TextareaControl, TextControl } from '@/frontend/features/Fde/FormControls'
import OntologySchemaFieldEditor, { type ContractField } from '@/frontend/features/Fde/OntologySchemaFieldEditor'
import { fdeRoutes } from '@/frontend/features/Fde/routes'
import type { Evidence, FdeCopy, OntologyInterface } from '@/frontend/features/Fde/types'

interface Props { open: boolean; onOpenChange: (open: boolean) => void; record: OntologyInterface | null; evidences: Evidence[]; projectId: string; copy: FdeCopy }

export default function OntologyInterfaceDialog({ open, onOpenChange, record, evidences, projectId, copy }: Props) {
  const form = useForm<{ name: string; description: string; property_schema: ContractField[]; change_note: string; evidence_id: string }>({ name: '', description: '', property_schema: [], change_note: '', evidence_id: '' })

  useEffect(() => {
    if (!open) return
    form.setData({
      name: record?.name ?? '',
      description: record?.description ?? '',
      property_schema: record?.property_schema.map(({ key, label, data_type, required }) => ({ key, label, data_type, required })) ?? [],
      change_note: '',
      evidence_id: '',
    })
  }, [open, record?.id])

  function submit() {
    form.transform((data) => ({ ontology_interface: data }))
    const options = { preserveScroll: true, onSuccess: () => onOpenChange(false) }
    const query = `?project_id=${projectId}`
    if (record) form.patch(`${fdeRoutes.ontologyInterfaces}/${record.id}${query}`, options)
    else form.post(`${fdeRoutes.ontologyInterfaces}${query}`, options)
  }

  return (
    <FdeEditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={record ? copy.workspace.edit_interface : copy.workspace.add_interface}
      closeLabel={copy.common.close}
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>{copy.common.cancel}</Button><Button disabled={form.processing || !form.data.name.trim()} onClick={submit}>{copy.common.save}</Button></>}
    >
        <div className="grid gap-5">
          <p className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs leading-5 text-muted-foreground">{copy.workspace.interface_description}</p>
          <TextControl label={copy.forms.name} value={form.data.name} onChange={(event) => form.setData('name', event.target.value)} required />
          <TextareaControl label={copy.forms.description} value={form.data.description} onChange={(event) => form.setData('description', event.target.value)} rows={3} />
          <OntologySchemaFieldEditor fields={form.data.property_schema} onChange={(value) => form.setData('property_schema', value)} title={copy.workspace.standard_properties} copy={copy} />
          <ChangeContextFields changeNote={form.data.change_note} evidenceId={form.data.evidence_id} evidences={evidences} copy={copy} onChangeNote={(value) => form.setData('change_note', value)} onEvidenceId={(value) => form.setData('evidence_id', value)} />
        </div>
    </FdeEditorDialog>
  )
}
